/* eslint-disable no-nested-ternary */
// src/components/filters/PopulationFilters.jsx
/* eslint-disable react/prop-types */
import React from 'react';
import {
    Accordion, AccordionDetails, AccordionSummary,
    Autocomplete, Box, Chip, FormControl, FormControlLabel,
    FormLabel, IconButton, Radio, RadioGroup, Stack, TextField, Typography
} from '@mui/material';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import Iconify from "../Iconify"; // adapte le chemin si besoin
import LucideIcon from '../LucideIcon';

function SectionTitle({ children, ...props }) {
    return (
        <Typography variant="subtitle1" paddingBottom={1} paddingTop={2} {...props}>
            {children}
        </Typography>
    );
}

function NumberRange({ label, value, onChange, onClear, caption, icon }) {
    const min = value?.min ?? '';
    const max = value?.max ?? '';
    const has = min !== '' || max !== '';
    return (
        <Accordion sx={{ bgcolor: '#f2f2f2', mb: 2 }}>
            <AccordionSummary expandIcon={<Iconify icon="ep:arrow-down-bold" />}>
                <Stack spacing={1} direction="row" alignItems="center">
                    <LucideIcon name={icon} />
                    <Typography>{label}</Typography>
                    {has && (
                        <Stack spacing={1} direction="row" alignItems="center">
                            <Typography sx={{ color: '#5E17EB' }} variant="caption">
                                {min}-{max}
                            </Typography>
                            <IconButton onClick={(e) => { onClear(); e.stopPropagation(); }}>
                                <Iconify icon="typcn:delete" sx={{ width: 15, height: 15 }} />
                            </IconButton>
                        </Stack>
                    )}
                </Stack>
            </AccordionSummary>
            <AccordionDetails>
                {caption && <Typography variant="caption">{caption}</Typography>}
                <Stack py={1} spacing={2} direction="row" alignItems="center">
                    <TextField
                        placeholder="min"
                        value={min}
                        onChange={(e) => onChange({ min: e.target.value, max })}
                        size="small"
                    />
                    <Typography variant="body2">à</Typography>
                    <TextField
                        placeholder="max"
                        value={max}
                        onChange={(e) => onChange({ min, max: e.target.value })}
                        size="small"
                    />
                </Stack>
            </AccordionDetails>
        </Accordion>
    );
}

function DateRange({ label, value, onChange, onClear, caption, icon = "bitcoin-icons:cart-outline" }) {
    const { after = null, before = null } = value || {};
    const has = after !== null || before !== null;
    return (
        <Accordion sx={{ bgcolor: '#f2f2f2', mb: 2 }}>
            <AccordionSummary expandIcon={<Iconify icon="ep:arrow-down-bold" />}>
                <Stack spacing={1} direction="row" alignItems="center">
                    <LucideIcon name={icon} />
                    <Typography>{label}</Typography>
                    {has && (
                        <Stack spacing={1} direction="row" alignItems="center">
                            <Typography sx={{ color: '#5E17EB' }} variant="caption">
                                {before && after ? `Entre ${after} et ${before}` : before ? `Avant ${before}` : after ? `Après ${after}` : ''}
                            </Typography>
                            <IconButton onClick={(e) => { onClear(); e.stopPropagation(); }}>
                                <Iconify icon="typcn:delete" sx={{ width: 15, height: 15 }} />
                            </IconButton>
                        </Stack>
                    )}
                </Stack>
            </AccordionSummary>
            <AccordionDetails>
                {caption && <Typography variant="caption">{caption}</Typography>}
                <Stack py={1} spacing={1} direction="column">
                    <Typography variant="body2">Après</Typography>
                    <TextField
                        type="date"
                        value={after || ''}
                        onChange={(e) => onChange({ after: e.target.value || null, before })}
                        size="small"
                    />
                    <Typography variant="body2">Avant</Typography>
                    <TextField
                        type="date"
                        value={before || ''}
                        onChange={(e) => onChange({ after, before: e.target.value || null })}
                        size="small"
                    />
                </Stack>
            </AccordionDetails>
        </Accordion>
    );
}

function BoolRadio({ label, value, onChange, onClear, caption, icon, yesLabel = 'Oui', noLabel = 'Non' }) {
    const has = value !== null && value !== undefined && value !== '';
    const toStr = (v) => (v === null ? '' : String(v));
    const toBool = (v) => v === true || v === 'true';

    return (
        <Accordion sx={{ bgcolor: '#f0f0f0', mb: 2 }}>
            <AccordionSummary expandIcon={<Iconify icon="ep:arrow-down-bold" />}>
                <Stack spacing={1} direction="row" alignItems="center">
                    <LucideIcon name={icon} />
                    <Typography>{label}</Typography>
                    {has && (
                        <Stack spacing={1} direction="row" alignItems="center">
                            <Typography sx={{ color: '#5E17EB' }} variant="caption">
                                {toBool(value) ? yesLabel : noLabel}
                            </Typography>
                            <IconButton onClick={(e) => { onClear(); e.stopPropagation(); }}>
                                <Iconify icon="typcn:delete" sx={{ width: 15, height: 15 }} />
                            </IconButton>
                        </Stack>
                    )}
                </Stack>
            </AccordionSummary>
            <AccordionDetails>
                <FormControl>
                    {caption && (
                        <FormLabel>
                            <Typography variant="caption">{caption}</Typography>
                        </FormLabel>
                    )}
                    <RadioGroup
                        value={toStr(value)}
                        onChange={(e) => onChange(e.target.value === 'true')}
                    >
                        <FormControlLabel value="true" control={<Radio />} label={yesLabel} />
                        <FormControlLabel value="false" control={<Radio />} label={noLabel} />
                    </RadioGroup>
                </FormControl>
            </AccordionDetails>
        </Accordion>
    );
}

export default function PopulationFilters({
    value,
    onChange,
    events = [],
    types = [],
    loadingEvents = false,
    loadingTypes = false,
    labels = {},
    title = 'Nouvelle population 1/2',
    sx,
}) {
    const filters = value || {
        filterEvent: [],
        filterInterest: [],
        filterNbEvent: { min: '', max: '' },
        filterBillet: { min: '', max: '' },
        totalDepense: { min: '', max: '' },
        lastDateOrder: { before: null, after: null },
        filterNewsletter: null,
        filterNotif: null,
        filterAbonne: null,
        filterHist: null,
        start: null,
        end: null,
    };

    const L = {
        eventsTitle: 'Événements',
        eventParticipation: 'Participations aux évènements',
        totalEventsLabel: "Nombre total d'évènements",
        totalEventsCaption: "Nombre minimum et/ou maximum d’évènements pour lesquels vos contacts ont acheté des billets.",
        interestsTitle: 'Intérêts',
        musicGenre: 'Genre musical',
        musicGenreCaption: "Contacts qui ont acheté des billets pour vos évènements étiquetés avec l’un de ces genres musicaux.",
        ordersTitle: 'Commandes',
        ticketsHeld: 'Nombre de billets détenus',
        ticketsHeldCaption: 'Nombre minimum et/ou maximum de billet acheté.',
        totalSpentLabel: 'Total dépensé',
        totalSpentCaption: 'Montant dépensé pour vos évènements.',
        lastPurchaseDate: 'Date de dernier achat',
        lastPurchaseCaption: "Les personnes qui ont acheté des billets pour vos évènements, soit avant une date spécifique, soit après celle-ci, ou dans le délai spécifié.",
        marketingTitle: 'Marketing',
        notifLabel: 'Abonné aux notifications ?',
        notifCaption: "Contacts ayant accepté de recevoir des notifications de votre organisation dans l'application Choptonticket",
        newsletterLabel: 'Abonné à la newsletter ?',
        newsletterCaption: 'Contacts abonnés à votre page organisateur sur Choptonticket',
        followLabel: 'Abonné à ma page Choptonticket ?',
        followCaption: 'Contacts abonnés à votre page organisateur sur Choptonticket',
        historyTitle: 'Historique',
        addedDate: "Date d'ajout",
        addedWhenCaption: 'Quand ce contact a été ajouté à votre communauté',
        ...labels,
    };

    const handle = (key, val) => onChange?.({ ...filters, [key]: val });

    return (
        <Box paddingBottom={3} paddingX={3} sx={{ height: { xs: '75vh', sm: '78vh', xl: '85vh' }, overflowX: 'auto', overflowY: 'auto', ...sx }}>
            <Typography variant="h5" pb={1} pt={1}>{title}</Typography>

            <SectionTitle>{L.eventsTitle}</SectionTitle>
            {/* Participations aux événements */}
            <Accordion sx={{ bgcolor: '#f2f2f2', mb: 2 }}>
                <AccordionSummary expandIcon={<Iconify icon="ep:arrow-down-bold" />}>
                    <Stack spacing={1} direction="row" alignItems="center">
                        <LucideIcon name='CalendarCheck' />
                        <Typography variant='body1'>{L.eventParticipation}</Typography>
                        {filters.filterEvent.length > 0 && (
                            <Stack spacing={1} direction="row" alignItems="center">
                                <Typography sx={{ color: '#5E17EB' }} variant="caption">
                                    {filters.filterEvent.length} sélectionné
                                </Typography>
                                <IconButton onClick={(e) => { handle('filterEvent', []); e.stopPropagation(); }}>
                                    <Iconify icon="typcn:delete" sx={{ width: 15, height: 15 }} />
                                </IconButton>
                            </Stack>
                        )}
                    </Stack>
                </AccordionSummary>
                <AccordionDetails>
                    {loadingEvents ? (
                        <Typography variant="body2">Chargement des évènements…</Typography>
                    ) : (
                        <Autocomplete
                            multiple
                            options={events}
                            getOptionLabel={(event) => event?.data?.()?.nomEvent ?? event?.data?.()?.name ?? 'Évènement'}
                            value={events.filter((e) => filters.filterEvent.includes(e.id))}
                            onChange={(e, newValue) => handle('filterEvent', newValue.map(ev => ev.id))}
                            renderOption={(props, option) => {
                                const d = option?.data?.() ?? option?.data?.();
                                return (
                                    <Stack key={option.id} direction="row" spacing={1} component="li" {...props} alignItems="center">
                                        {d?.idVisuel && (
                                            <LazyLoadImage
                                                src={d.idVisuel}
                                                alt={d?.nomEvent || d?.name}
                                                width={70}
                                                height={70}
                                                style={{ objectFit: 'cover' }}
                                                effect="blur"
                                            />
                                        )}
                                        <Box>
                                            <Typography variant="subtitle1">{d?.nomEvent || d?.name || 'Évènement'}</Typography>
                                            {typeof d?.participant === 'number' && (
                                                <Typography variant="body2" color="textSecondary">
                                                    {d.participant} participants
                                                </Typography>
                                            )}
                                        </Box>
                                    </Stack>
                                );
                            }}
                            renderInput={(params) => (
                                <TextField {...params} variant="outlined" placeholder={filters.filterEvent.length ? null : 'Sélectionner les évènements'} multiline />
                            )}
                            filterOptions={(options, { inputValue }) =>
                                options.filter((option) => (option?.data?.()?.nomEvent || option?.data?.()?.name || '')
                                    .toLowerCase()
                                    .includes(inputValue.toLowerCase()))
                            }
                            renderTags={(tagValue, getTagProps) =>
                                tagValue.map((event, index) => (
                                    <Chip
                                        sx={{ bgcolor: '#F5EFFA' }}
                                        key={event.id}
                                        label={event?.data?.()?.name || event?.data?.()?.nomEvent || 'Évènement'}
                                        {...getTagProps({ index })}
                                    />
                                ))
                            }
                        />
                    )}
                </AccordionDetails>
            </Accordion>

            {/* Nombre total d'évènements */}
            <NumberRange
                label={L.totalEventsLabel}
                caption={L.totalEventsCaption}
                value={filters.filterNbEvent}
                onChange={(v) => handle('filterNbEvent', v)}
                onClear={() => handle('filterNbEvent', { min: '', max: '' })}
            />

            <SectionTitle>{L.interestsTitle}</SectionTitle>
            {/* Genres */}
            <Accordion sx={{ bgcolor: '#f2f2f2', mb: 2 }}>
                <AccordionSummary expandIcon={<Iconify icon="ep:arrow-down-bold" />}>
                    <Stack spacing={1} direction="row" alignItems="center">
                        <Iconify icon="material-symbols-light:interests-outline" sx={{ minWidth: 20, minHeight: 20 }} />
                        <Typography>{L.musicGenre}</Typography>
                        {filters.filterInterest.length > 0 && (
                            <Stack spacing={1} direction="row" alignItems="center">
                                <Typography sx={{ color: '#5E17EB' }} variant="caption">
                                    {filters.filterInterest.length === 1
                                        ? filters.filterInterest[0]
                                        : `${filters.filterInterest[0]} + ${filters.filterInterest.length - 1}`}
                                </Typography>
                                <IconButton onClick={(e) => { handle('filterInterest', []); e.stopPropagation(); }}>
                                    <Iconify icon="typcn:delete" sx={{ width: 15, height: 15 }} />
                                </IconButton>
                            </Stack>
                        )}
                    </Stack>
                </AccordionSummary>
                <AccordionDetails>
                    <Typography variant="caption">{L.musicGenreCaption}</Typography>
                    <Box p={1}>
                        {loadingTypes ? (
                            <Typography variant="body2">Chargement des genres…</Typography>
                        ) : (
                            <Autocomplete
                                multiple
                                options={types}
                                getOptionLabel={(type) => type?.data?.().nom}
                                value={types.filter((t) => filters.filterInterest.includes(t?.data?.().nom))}
                                onChange={(e, newValue) =>
                                    handle('filterInterest', newValue.map((t) => t?.data?.().nom))
                                }
                                renderInput={(params) => (
                                    <TextField {...params} variant="outlined" placeholder={filters.filterInterest.length ? null : 'Sélectionner des intérêts'} multiline />
                                )}
                                renderTags={(tagValue, getTagProps) =>
                                    tagValue.map((t, i) => (
                                        <Chip
                                            sx={{ bgcolor: '#F5EFFA' }}
                                            key={`${t?.id || i}`}
                                            label={t?.data?.().nom}
                                            {...getTagProps({ index: i })}
                                        />
                                    ))
                                }
                            />
                        )}
                    </Box>
                </AccordionDetails>
            </Accordion>

            <SectionTitle>{L.ordersTitle}</SectionTitle>
            <NumberRange
            icon={'Ticket'}
                label={L.ticketsHeld}
                caption={L.ticketsHeldCaption}
                value={filters.filterBillet}
                onChange={(v) => handle('filterBillet', v)}
                onClear={() => handle('filterBillet', { min: '', max: '' })}
            />
            <NumberRange
                label={L.totalSpentLabel}
                caption={L.totalSpentCaption}
                value={filters.totalDepense}
                icon={'HandCoins'}
                onChange={(v) => handle('totalDepense', v)}
                onClear={() => handle('totalDepense', { min: '', max: '' })}
            />

            <DateRange
                label={L.lastPurchaseDate}
                caption={L.lastPurchaseCaption}
                value={filters.lastDateOrder}
                onChange={(v) => handle('lastDateOrder', v)}
                onClear={() => handle('lastDateOrder', { before: null, after: null })}
                icon={'ShoppingCart'}
            />

            <SectionTitle>{L.marketingTitle}</SectionTitle>
            <BoolRadio
                label={L.notifLabel}
                caption={L.notifCaption}
                value={filters.filterNotif}
                onChange={(v) => handle('filterNotif', v)}
                onClear={() => handle('filterNotif', null)}
                icon="BellRing"
            />
            <BoolRadio
                label={L.newsletterLabel}
                caption={L.newsletterCaption}
                value={filters.filterNewsletter}
                onChange={(v) => handle('filterNewsletter', v)}
                onClear={() => handle('filterNewsletter', null)}
                icon="Mails"
            />
            <BoolRadio
                label={L.followLabel}
                caption={L.followCaption}
                value={filters.filterAbonne}
                onChange={(v) => handle('filterAbonne', v)}
                onClear={() => handle('filterAbonne', null)}
                icon="Heart"
            />

            <SectionTitle>{L.historyTitle}</SectionTitle>
            <Accordion sx={{ bgcolor: '#f2f2f2', mb: 2 }}>
                <AccordionSummary expandIcon={<Iconify icon="ep:arrow-down-bold" />}>
                    <Stack spacing={1} direction="row" alignItems="center">
                        <LucideIcon name='History'/>
                        <Typography>{L.addedDate}</Typography>
                        {filters?.filterHist && (
                            <Stack spacing={1} direction="row" alignItems="center">
                                <Typography sx={{ color: '#5E17EB' }} variant="caption">
                                    {filters.filterHist === 'custom' && filters.start && filters.end
                                        ? `${filters.start} - ${filters.end}`
                                        : filters.filterHist === 'today' ? "Aujourd'hui"
                                            : filters.filterHist === 'yesterday' ? 'Hier'
                                                : filters.filterHist === 'last-7-days' ? 'Derniers 7 jours'
                                                    : filters.filterHist === 'last-14-days' ? 'Derniers 14 jours'
                                                        : filters.filterHist === 'last-30-days' ? 'Derniers 30 jours'
                                                            : null}
                                </Typography>
                                <IconButton onClick={(e) => { handle('filterHist', null); e.stopPropagation(); }}>
                                    <Iconify icon="typcn:delete" sx={{ width: 15, height: 15 }} />
                                </IconButton>
                            </Stack>
                        )}
                    </Stack>
                </AccordionSummary>
                <AccordionDetails>
                    <FormControl>
                        <FormLabel>
                            <Typography variant="caption">{L.addedWhenCaption}</Typography>
                        </FormLabel>
                        <RadioGroup
                            value={filters?.filterHist || ''}
                            onChange={(e) => handle('filterHist', e.target.value)}
                        >
                            <FormControlLabel value="today" control={<Radio />} label="Aujourd'hui" />
                            <FormControlLabel value="yesterday" control={<Radio />} label="Hier" />
                            <FormControlLabel value="last-7-days" control={<Radio />} label="Derniers 7 jours" />
                            <FormControlLabel value="last-14-days" control={<Radio />} label="Derniers 14 jours" />
                            <FormControlLabel value="last-30-days" control={<Radio />} label="Derniers 30 jours" />
                            <FormControlLabel value="custom" control={<Radio />} label="Sélectionner la plage" />
                        </RadioGroup>
                    </FormControl>

                    {filters?.filterHist === 'custom' && (
                        <Stack py={1} spacing={1}>
                            <Typography variant="body2">Début</Typography>
                            <TextField
                                type="date"
                                value={filters?.start || ''}
                                onChange={(e) => handle('start', e.target.value || null)}
                                size="small"
                            />
                            <Typography variant="body2">Fin</Typography>
                            <TextField
                                type="date"
                                value={filters?.end || ''}
                                onChange={(e) => handle('end', e.target.value || null)}
                                size="small"
                            />
                        </Stack>
                    )}
                </AccordionDetails>
            </Accordion>
        </Box>
    );
}
